import type {
  Control,
  FieldPath,
  FieldPathValue,
  FieldValues
} from "react-hook-form";

import type { AddressShape } from "@/lib/forms/schema";

import { CountryField } from "./CountryField";
import { FormRow } from "./Form";
import { TextField } from "./TextField";

/** Paths in `T` whose value is an address object. */
export type AddressPath<T extends FieldValues> = {
  [K in FieldPath<T>]: FieldPathValue<T, K> extends AddressShape ? K : never;
}[FieldPath<T>];

type Props<T extends FieldValues> = {
  control?: Control<T>;
  /** Path of the address object, e.g. `"address"` or `"employer.address"`. */
  name: AddressPath<T>;
  required?: boolean;
  disabled?: boolean;
  /** Overrides the shared country list. */
  countries?: ReadonlyArray<string>;
};

/** Street / city / state / ZIP / country in the standard layout. */
export function AddressFields<T extends FieldValues>({
  control,
  name,
  required = false,
  disabled = false,
  countries
}: Props<T>) {
  const path = (part: keyof AddressShape) => `${name}.${part}` as FieldPath<T>;
  return (
    <>
      <TextField
        control={control}
        name={path("street")}
        label="Street Address"
        placeholder="Enter street address"
        autoComplete="street-address"
        required={required}
        disabled={disabled}
      />
      <FormRow>
        <TextField
          control={control}
          name={path("city")}
          label="City"
          placeholder="Enter city"
          autoComplete="address-level2"
          required={required}
          disabled={disabled}
        />
        <TextField
          control={control}
          name={path("state")}
          label="State/Province"
          placeholder="Enter state"
          autoComplete="address-level1"
          required={required}
          disabled={disabled}
        />
        <TextField
          control={control}
          name={path("zipCode")}
          label="ZIP/Postal Code"
          placeholder="Enter ZIP code"
          autoComplete="postal-code"
          required={required}
          disabled={disabled}
        />
      </FormRow>
      <CountryField
        control={control}
        name={path("country")}
        label="Country"
        options={countries}
        required={required}
        disabled={disabled}
      />
    </>
  );
}
